/**
 * Budget line picker for project-part budget assignment.
 *
 * Replaces the older multi-step AddBudgetDialog with a fast table picker:
 *  - Quick label filter chips at the top
 *  - Title search
 *  - Table of every investment line on the building with Status, Titel,
 *    Label, Units, Strategisch budget, In Project, Overflow and Aangemaakt
 *  - Multi-select with a per-row amount input (defaults to the remaining
 *    strategic headroom). One POST per selected line.
 *
 * Drop-in for AddBudgetDialog: same props, same `onSuccess()` semantics so
 * the parent re-fetches the part / project after the assignments land.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  CurrencyEur,
  MagnifyingGlass,
  Tag,
  Cube,
  CheckSquare,
  Square,
  X,
  ArrowUUpLeft,
  Lock,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ProjectPart, RenovationLabel } from "shared/types/renovation";
import type { InvestmentItem } from "screens/investment/types/investment";
import { InvestmentItemStatus } from "screens/investment/types/investment";

interface BudgetLinePickerModalProps {
  part: ProjectPart;
  buildingId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatDate = (value?: string | Date) => {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("nl-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const STATUS_TONE: Record<string, string> = {
  [InvestmentItemStatus.ONTWERP]: "bg-gray-100 text-gray-700 border-gray-200",
  [InvestmentItemStatus.IN_BEOORDELING]:
    "bg-blue-100 text-blue-700 border-blue-200",
  [InvestmentItemStatus.GOEDGEKEURD]:
    "bg-green-100 text-green-700 border-green-200",
  [InvestmentItemStatus.AFGEWEZEN]: "bg-red-100 text-red-700 border-red-200",
  [InvestmentItemStatus.IN_PROJECT]:
    "bg-moss-primary/10 text-moss-primary border-moss-primary/30",
  [InvestmentItemStatus.CLOSED]:
    "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const LABEL_TONE: Record<string, string> = {
  esthetisch: "border-pink-200 bg-pink-50 text-pink-700",
  energetisch: "border-emerald-200 bg-emerald-50 text-emerald-700",
  veiligheid: "border-rose-200 bg-rose-50 text-rose-700",
  comfort: "border-blue-200 bg-blue-50 text-blue-700",
  technisch: "border-amber-200 bg-amber-50 text-amber-700",
  algemeen: "border-neutral-200 bg-neutral-50 text-neutral-700",
};

interface SelectionDraft {
  itemId: number;
  amount: number;
  /**
   * Per-unit % share of `amount`. Sum should be 100. We initialise this to
   * an even split across the part's kostenplaatsen on selection so users get
   * something sensible immediately and only need to override outliers.
   */
  unitPercentages: Record<string, number>;
}

/** Equal split helper — last unit absorbs rounding so the sum is exactly 100. */
const equalSplitPercentages = (unitIds: string[]): Record<string, number> => {
  const out: Record<string, number> = {};
  if (unitIds.length === 0) return out;
  const each = 100 / unitIds.length;
  let assigned = 0;
  unitIds.forEach((id, idx) => {
    const v =
      idx === unitIds.length - 1
        ? +(100 - assigned).toFixed(2)
        : +each.toFixed(2);
    out[id] = v;
    assigned += v;
  });
  return out;
};

export const BudgetLinePickerModal = ({
  part,
  buildingId,
  isOpen,
  onClose,
  onSuccess,
}: BudgetLinePickerModalProps) => {
  const { session } = useSession();

  const [items, setItems] = useState<InvestmentItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [search, setSearch] = useState("");
  const [activeLabelIds, setActiveLabelIds] = useState<Set<number>>(new Set());
  const [selection, setSelection] = useState<Map<number, SelectionDraft>>(
    new Map(),
  );

  // Reset draft state every open. Default the label filter to *the part's
  // own label* so the table immediately surfaces matching budget lines —
  // the most common picker case. The chip stays toggleable so the user can
  // widen / narrow the filter from there. The part-label chip is also
  // visually distinct (see render below) so the user sees which one
  // matches.
  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setActiveLabelIds(
      part.label?.id ? new Set<number>([part.label.id]) : new Set<number>(),
    );
    setSelection(new Map());
  }, [isOpen, part.id, part.label?.id]);

  useEffect(() => {
    if (!isOpen || !session || !buildingId) return;
    let cancelled = false;
    setIsLoading(true);
    fetchWithAuth(
      `${API_URL}/investment/items?buildingId=${buildingId}&excludeConverted=false`,
      { method: "GET" },
      session,
    )
      .then(async (itemsRes) => {
        if (cancelled) return;
        if (itemsRes.ok) {
          const data = await itemsRes.json();
          setItems(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
        }
      })
      .catch((err) => {
        console.error("[BudgetLinePickerModal] fetch failed", err);
        toast.error("Fout bij ophalen budgetlijnen");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, session, buildingId]);

  // The part's own kostenplaatsen (provisional units) are the source of
  // truth for distribution — those are the units the PM already locked in
  // for this part. Fall back to the project's units when a part hasn't been
  // configured yet, so the picker still works on a fresh part.
  const partUnitIds = useMemo<string[]>(() => {
    const fromPart = (part.provisionalUnits ?? [])
      .map((pu) => pu.unit?.id)
      .filter((id): id is string => !!id);
    if (fromPart.length > 0) return fromPart;
    const fromProject =
      ((part.project as any)?.units as Array<{ id?: string }> | undefined) ?? [];
    return fromProject.map((u) => u?.id).filter((id): id is string => !!id);
  }, [part]);

  // Distinct labels actually used by visible items, plus part.label as a
  // pinned chip even if no item carries it yet.
  const labelsInUse = useMemo(() => {
    const map = new Map<number, RenovationLabel>();
    if (part.label?.id) map.set(part.label.id, part.label as RenovationLabel);
    for (const item of items) {
      if (item.label?.id && !map.has(item.label.id)) {
        map.set(item.label.id, item.label as RenovationLabel);
      }
    }
    // Fall back to alphabetical when many labels exist.
    return Array.from(map.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
  }, [items, part.label]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      // Hide released — they're locked and can't accept new assignments.
      if (item.isReleased) return false;
      if (
        activeLabelIds.size > 0 &&
        (!item.label?.id || !activeLabelIds.has(item.label.id))
      ) {
        return false;
      }
      if (term && !(item.title || "").toLowerCase().includes(term)) {
        return false;
      }
      return true;
    });
  }, [items, activeLabelIds, search]);

  const toggleSelection = (item: InvestmentItem) => {
    setSelection((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        const total = Number(item.estimatedCostExclVat) || 0;
        const used = Number(item.allocatedBudget) || 0;
        const remaining = Math.max(0, total - used);
        next.set(item.id, {
          itemId: item.id,
          amount: remaining > 0 ? Math.round(remaining) : 0,
          // Auto-equal split across the part's kostenplaatsen. User can
          // override per unit before submitting.
          unitPercentages: equalSplitPercentages(partUnitIds),
        });
      }
      return next;
    });
  };

  const setSelectionAmount = (itemId: number, amount: number) => {
    setSelection((prev) => {
      const next = new Map(prev);
      const draft = next.get(itemId);
      if (draft) next.set(itemId, { ...draft, amount });
      return next;
    });
  };

  const setUnitPercentage = (itemId: number, unitId: string, pct: number) => {
    setSelection((prev) => {
      const next = new Map(prev);
      const draft = next.get(itemId);
      if (!draft) return next;
      const safe = Math.max(0, Math.min(100, Number(pct) || 0));
      next.set(itemId, {
        ...draft,
        unitPercentages: { ...draft.unitPercentages, [unitId]: safe },
      });
      return next;
    });
  };

  const splitEvenlyOnLine = (itemId: number) => {
    setSelection((prev) => {
      const next = new Map(prev);
      const draft = next.get(itemId);
      if (!draft) return next;
      next.set(itemId, {
        ...draft,
        unitPercentages: equalSplitPercentages(partUnitIds),
      });
      return next;
    });
  };

  const totalToAssign = useMemo(() => {
    let sum = 0;
    selection.forEach((s) => (sum += Number(s.amount) || 0));
    return sum;
  }, [selection]);

  const validSelection = useMemo(() => {
    const drafts: SelectionDraft[] = [];
    selection.forEach((s) => {
      if (Number(s.amount) > 0) drafts.push(s);
    });
    return drafts;
  }, [selection]);

  const canSubmit =
    !isSubmitting && validSelection.length > 0 && partUnitIds.length > 0;

  const handleSubmit = async () => {
    if (!session || validSelection.length === 0) return;
    if (partUnitIds.length === 0) {
      toast.error(
        "Voeg eerst kostenplaatsen toe aan dit projectonderdeel voordat je budget koppelt.",
      );
      return;
    }
    setIsSubmitting(true);
    let successCount = 0;
    try {
      for (const draft of validSelection) {
        // We deliberately don't send `budgetIncreaseAmount` here even when
        // the requested amount is larger than the line's strategic
        // headroom. The BE simply grows `allocatedBudget`; the line's
        // strategic budget stays put and the excess (allocated − strategic)
        // surfaces in the Overflow column / overflow bucket.
        const body: Record<string, unknown> = {
          investmentItemId: draft.itemId,
          amount: draft.amount,
          unitIds: partUnitIds,
          unitDistribution: draft.unitPercentages,
        };

        const response = await fetchWithAuth(
          `${API_URL}/renovation/project/part/${part.id}/investment-assignment`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          },
          session,
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || `Failed to assign line ${draft.itemId}`);
        }
        successCount += 1;
      }

      toast.success(
        `${successCount} budgetlijn${successCount === 1 ? "" : "en"} gekoppeld`,
      );
      onSuccess();
      onClose();
    } catch (err) {
      console.error("[BudgetLinePickerModal] submit error", err);
      toast.error(
        err instanceof Error
          ? err.message
          : "Fout bij koppelen van budgetlijnen",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleLabelFilter = (id: number) => {
    setActiveLabelIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent className="flex h-[90vh] max-h-[90vh] max-w-[1200px] flex-col p-0 sm:max-w-[1200px]">
        <DialogHeader className="border-b border-neutral-200 px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <CurrencyEur className="h-5 w-5 text-moss-primary" />
            Budgetlijn koppelen aan {part.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-secondary-mid">
            Kies één of meerdere budgetlijnen en pas eventueel het bedrag aan
            dat naar dit projectonderdeel gaat. Een bedrag boven het strategisch
            budget komt automatisch in de overflow-pot terecht.
          </DialogDescription>
        </DialogHeader>

        {/* Toolbar — search + label filter chips */}
        <div className="border-b border-neutral-200 bg-neutral-50 px-6 py-3 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass
                size={14}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Zoek op titel..."
                className="h-9 pl-7 text-sm"
              />
            </div>
            {activeLabelIds.size > 0 && (
              <button
                type="button"
                onClick={() => setActiveLabelIds(new Set())}
                className="text-xs text-neutral-500 hover:text-rose-600 inline-flex items-center gap-1"
              >
                <X size={12} /> Filters wissen
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <Tag
              size={12}
              weight="duotone"
              className="text-neutral-secondary-mid"
            />
            <span className="text-neutral-secondary-mid text-[10px] font-semibold uppercase tracking-wide">
              Labels
            </span>
            {labelsInUse.length === 0 ? (
              <span className="text-neutral-secondary-mid text-[11px] italic">
                Geen labels op deze budgetlijnen
              </span>
            ) : (
              labelsInUse.map((l) => {
                const active = activeLabelIds.has(l.id);
                const tone =
                  LABEL_TONE[(l as any).type?.toLowerCase?.() ?? "algemeen"] ??
                  LABEL_TONE.algemeen;
                // The part's *own* label gets a distinct moss-primary ring +
                // "match" suffix so the user sees at a glance which chip
                // mirrors the project part's category.
                const isPartLabel = part.label?.id === l.id;
                const baseClasses =
                  "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors";
                const classes = active
                  ? isPartLabel
                    ? "bg-moss-primary/15 border-moss-primary text-moss-primary ring-1 ring-moss-primary/40 shadow-sm"
                    : tone
                  : isPartLabel
                  ? "bg-moss-primary/5 border-moss-primary/40 text-moss-primary hover:bg-moss-primary/10"
                  : "border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300";
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => toggleLabelFilter(l.id)}
                    className={`${baseClasses} ${classes}`}
                    title={
                      isPartLabel
                        ? "Categorie van dit projectonderdeel — automatisch geselecteerd"
                        : undefined
                    }
                  >
                    <Tag size={10} weight="fill" />
                    {l.name}
                    {isPartLabel && (
                      <span className="ml-0.5 text-[9px] uppercase tracking-wide opacity-80">
                        match
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Body — table */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="text-neutral-secondary-mid py-12 text-center text-sm">
              Budgetlijnen laden…
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-neutral-secondary-mid py-12 text-center text-sm">
              Geen budgetlijnen gevonden voor de huidige filters.
            </div>
          ) : (
            <table className="w-full table-fixed text-sm">
              <thead className="sticky top-0 z-10 bg-white text-[10px] font-semibold uppercase tracking-wide text-neutral-secondary-mid shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.05)]">
                <tr>
                  <th className="w-10 px-2 py-2 text-center">
                    <span className="sr-only">Selecteer</span>
                  </th>
                  <th className="w-[100px] px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-left">Titel</th>
                  <th className="w-[140px] px-2 py-2 text-left">Label</th>
                  <th className="w-[140px] px-2 py-2 text-left">Units</th>
                  {/* "Projectbudget" sits before the financial columns so the
                      input lands closer to the user's eye line. */}
                  <th className="w-[140px] px-2 py-2 text-right">
                    Projectbudget
                  </th>
                  <th className="w-[120px] px-2 py-2 text-right">
                    Strategisch
                  </th>
                  <th className="w-[110px] px-2 py-2 text-right">
                    Gekoppeld aan alle projecten
                  </th>
                  <th className="w-[100px] px-2 py-2 text-right">Overflow</th>
                  <th className="w-[100px] px-2 py-2 text-left">Aangemaakt</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => {
                  const total = Number(item.estimatedCostExclVat) || 0;
                  const used = Number(item.allocatedBudget) || 0;
                  const remaining = Math.max(0, total - used);
                  const overflow = Math.max(0, used - total);
                  const isSelected = selection.has(item.id);
                  const draft = selection.get(item.id);
                  const overflowFromDraft = draft
                    ? Math.max(0, draft.amount - remaining)
                    : 0;

                  const labelTone = item.label?.type
                    ? LABEL_TONE[item.label.type.toLowerCase()] ??
                      LABEL_TONE.algemeen
                    : LABEL_TONE.algemeen;

                  const unitIds =
                    item.units?.map((u) => u.id).filter(Boolean) ?? [];

                  return (
                    <React.Fragment key={item.id}>
                    <tr
                      onClick={() => toggleSelection(item)}
                      className={`cursor-pointer border-b border-neutral-100 transition-colors ${
                        isSelected
                          ? "bg-moss-primary/5 hover:bg-moss-primary/10"
                          : "hover:bg-neutral-50"
                      }`}
                    >
                      <td className="px-2 py-2 text-center align-top">
                        {isSelected ? (
                          <CheckSquare
                            size={18}
                            weight="fill"
                            className="text-moss-primary"
                          />
                        ) : (
                          <Square
                            size={18}
                            className="text-neutral-300"
                          />
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                            STATUS_TONE[item.status] ??
                            "bg-gray-100 text-gray-700 border-gray-200"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="font-medium text-neutral-primary line-clamp-2">
                          {item.title}
                        </div>
                        {item.description && (
                          <div className="text-[10px] text-neutral-400 line-clamp-1">
                            {item.description}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">
                        {item.label ? (
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${labelTone}`}
                          >
                            <Tag size={10} weight="fill" />
                            <span className="truncate max-w-[110px]">
                              {item.label.name}
                            </span>
                          </span>
                        ) : (
                          <span className="text-[10px] text-neutral-400 italic">
                            geen label
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">
                        {unitIds.length === 0 ? (
                          <span className="text-[10px] text-neutral-400 italic">
                            geen
                          </span>
                        ) : (
                          <div
                            className="flex flex-wrap gap-1"
                            title={unitIds.join(", ")}
                          >
                            {unitIds.slice(0, 2).map((id) => (
                              <span
                                key={id}
                                className="bg-moss-primary/10 text-moss-primary inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium"
                              >
                                <Cube size={9} weight="duotone" />
                                {id}
                              </span>
                            ))}
                            {unitIds.length > 2 && (
                              <span className="text-[10px] text-neutral-400">
                                +{unitIds.length - 2}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      {/* Projectbudget — moved before Strategisch so the input
                          sits closer to the user's eye line. */}
                      <td
                        className="px-2 py-2 align-top"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isSelected ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <div className="relative w-full">
                              <span className="text-neutral-secondary-mid pointer-events-none absolute inset-y-0 left-2 flex items-center text-xs">
                                €
                              </span>
                              <Input
                                type="number"
                                min={0}
                                step={100}
                                // Empty-string when the draft is 0 so the user
                                // can type without fighting a leading "0".
                                value={draft?.amount ? draft.amount : ""}
                                onChange={(e) =>
                                  setSelectionAmount(
                                    item.id,
                                    Number(e.target.value || 0),
                                  )
                                }
                                onFocus={(e) => e.target.select()}
                                placeholder="0"
                                className="h-7 pl-5 text-right text-xs tabular-nums"
                              />
                            </div>
                            {overflowFromDraft > 0 && (
                              <span className="text-[9px] text-amber-700 font-medium">
                                +{formatCurrency(overflowFromDraft)} naar
                                overflow
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-end gap-0.5 opacity-60">
                            <span className="text-[10px] text-neutral-400">
                              klik op rij
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right align-top tabular-nums">
                        {formatCurrency(total)}
                      </td>
                      <td className="px-2 py-2 text-right align-top tabular-nums">
                        <div className="text-neutral-primary">
                          {formatCurrency(used)}
                        </div>
                        <div className="text-[10px] text-neutral-400">
                          rest {formatCurrency(remaining)}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right align-top tabular-nums">
                        {overflow > 0 ? (
                          <span className="text-amber-700">
                            {formatCurrency(overflow)}
                          </span>
                        ) : (
                          <span className="text-neutral-300">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top text-[11px] text-neutral-500">
                        {formatDate(item.createdAt)}
                      </td>
                    </tr>

                    {/* Distribution sub-row — shown only when this line is
                        selected. The amount typed above is split across the
                        part's kostenplaatsen with editable per-unit %. */}
                    {isSelected && (
                      <tr className="bg-moss-primary/[0.03] border-b border-neutral-100">
                        <td colSpan={10} className="px-4 py-3">
                          <UnitDistributionEditor
                            unitIds={partUnitIds}
                            amount={draft?.amount || 0}
                            unitPercentages={draft?.unitPercentages ?? {}}
                            onChange={(uid, pct) =>
                              setUnitPercentage(item.id, uid, pct)
                            }
                            onResetEqual={() => splitEvenlyOnLine(item.id)}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="border-t border-neutral-200 px-6 py-3">
          <div className="flex w-full items-center justify-between">
            <div className="text-xs text-neutral-secondary-mid">
              {selection.size === 0 ? (
                <span>Geen lijnen geselecteerd</span>
              ) : (
                <span>
                  <strong className="text-neutral-primary">
                    {selection.size}
                  </strong>{" "}
                  lijn{selection.size === 1 ? "" : "en"} ·{" "}
                  <strong className="text-neutral-primary">
                    {formatCurrency(totalToAssign)}
                  </strong>{" "}
                  totaal
                </span>
              )}
              {partUnitIds.length === 0 && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700">
                  <Lock size={10} weight="fill" />
                  Voeg eerst kostenplaatsen toe aan dit projectonderdeel
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Annuleren
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="bg-moss-primary text-white hover:bg-moss-primary/90"
              >
                <ArrowUUpLeft className="mr-1 h-4 w-4 rotate-180" weight="bold" />
                {isSubmitting
                  ? "Koppelen…"
                  : `Koppel ${validSelection.length} lijn${
                      validSelection.length === 1 ? "" : "en"
                    }`}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Per-unit distribution editor — rendered as a sub-row below a selected line.
// ============================================================================

interface UnitDistributionEditorProps {
  unitIds: string[];
  amount: number;
  unitPercentages: Record<string, number>;
  onChange: (unitId: string, pct: number) => void;
  onResetEqual: () => void;
}

const UnitDistributionEditor: React.FC<UnitDistributionEditorProps> = ({
  unitIds,
  amount,
  unitPercentages,
  onChange,
  onResetEqual,
}) => {
  const sumPct = unitIds.reduce(
    (s, uid) => s + (unitPercentages[uid] ?? 0),
    0,
  );
  const mismatch = unitIds.length > 0 && Math.abs(sumPct - 100) > 0.5;

  if (unitIds.length === 0) {
    return (
      <div className="text-xs italic text-rose-600">
        Geen kostenplaatsen op dit projectonderdeel — voeg er eerst toe via de
        sectie "Voorziene kostenplaatsen".
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-secondary-mid">
          Verdeling over kostenplaatsen
          <span className="ml-2 text-neutral-400 italic font-normal normal-case tracking-normal">
            standaard gelijk verdeeld — pas % aan om de verdeling te wijzigen
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-[10px] font-medium tabular-nums ${
              mismatch ? "text-rose-600" : "text-neutral-secondary-mid"
            }`}
          >
            Σ {sumPct.toFixed(1)}%
          </span>
          <button
            type="button"
            onClick={onResetEqual}
            className="text-[10px] font-medium text-moss-primary hover:underline"
          >
            Gelijk verdelen
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {unitIds.map((uid) => {
          const pct = unitPercentages[uid] ?? 0;
          const unitAmount = Math.round(((amount || 0) * pct) / 100);
          return (
            <div
              key={uid}
              className="bg-white border-moss-primary/30 flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px]"
            >
              <Cube
                size={11}
                weight="duotone"
                className="text-moss-primary shrink-0"
              />
              <span className="text-moss-primary font-semibold tabular-nums">
                {uid}
              </span>
              <div className="bg-moss-primary/20 mx-0.5 h-5 w-px" />
              <span className="text-neutral-500 text-[10px] tabular-nums w-14 text-right">
                €
                {new Intl.NumberFormat("nl-BE", {
                  maximumFractionDigits: 0,
                }).format(unitAmount)}
              </span>
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={Number.isFinite(pct) ? +pct.toFixed(1) : 0}
                onChange={(e) => onChange(uid, Number(e.target.value || 0))}
                className="h-6 w-12 rounded border border-neutral-200 bg-white px-1 text-right text-[11px] tabular-nums focus:border-moss-primary focus:outline-none"
                onClick={(e) => e.stopPropagation()}
              />
              <span className="text-neutral-secondary-mid text-[10px]">%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BudgetLinePickerModal;
